import {
  type Row,
  type RowData,
  type RowModel,
  type Table,
  type TableFeatures,
  type Table_Internal,
  tableMemo,
} from '@tanstack/react-table';

import { row_getIsExpanded } from './SubRows.js';

// Overwrite the default `getExpandedRowModel` with our custom implementation that adds support for our implementation of getIsExpanded custom logic.
export function createExpandedRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(): (table: Table<TFeatures, TData>) => () => RowModel<TFeatures, TData> {
  return (_table) => {
    const table = _table as Table_Internal<TFeatures, TData>;
    return tableMemo({
      feature: 'rowExpandingFeature',
      table,
      fnName: 'table.getExpandedRowModel',
      memoDeps: () => [
        table.atoms.expanded?.get(),
        table.getPreExpandedRowModel(),
        table.options.paginateExpandedRows,
      ],
      fn: () => _createExpandedRowModel(table),
    });
  };
}

function _createExpandedRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(table: Table_Internal<TFeatures, TData>): RowModel<TFeatures, TData> {
  const rowModel = table.getPreExpandedRowModel();
  const expanded = table.atoms.expanded?.get();

  if (
    !rowModel.rows.length ||
    (expanded !== true && !Object.keys(expanded ?? {}).length)
  ) {
    return rowModel;
  }

  if (!table.options.paginateExpandedRows) {
    // Only expand rows at this point if they are being paginated
    return rowModel;
  }

  return expandRows(rowModel);
}

export function expandRows<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(rowModel: RowModel<TFeatures, TData>): RowModel<TFeatures, TData> {
  const expandedRows: Array<Row<TFeatures, TData>> = [];

  const handleRow = (row: Row<TFeatures, TData>) => {
    expandedRows.push(row);

    if (row.subRows.length && row_getIsExpanded(row)) {
      row.subRows.forEach(handleRow);
    }
  };

  rowModel.rows.forEach(handleRow);

  return {
    rows: expandedRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById,
  };
}
